import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Promise-based confirm dialog using shadcn AlertDialog.
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: "Delete?", description: "Cannot undo." });
 *   if (!ok) return;
 *
 * Supports a "type to confirm" guard for destructive flows:
 *   await confirm({ title: "Delete ALL parts", requireTypedText: "DELETE ALL" });
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, description, confirmText, cancelText, destructive, requireTypedText, numericInput }
  const [typed, setTyped] = useState("");
  const [numeric, setNumeric] = useState("");
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setTyped("");
      setNumeric(opts?.numericInput?.defaultValue != null ? String(opts.numericInput.defaultValue) : "");
      setState({
        title: "Are you sure?",
        description: "",
        confirmText: "Confirm",
        cancelText: "Cancel",
        destructive: false,
        requireTypedText: null,
        numericInput: null, // { label, min?, max?, defaultValue? } → resolve with parsed number or false on cancel
        ...opts,
      });
    });
  }, []);

  const close = (result) => {
    setState(null);
    setTyped("");
    setNumeric("");
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  const ctx = useMemo(() => ({ confirm }), [confirm]);
  const open = state !== null;
  const needsTyped = state?.requireTypedText;
  const numericCfg = state?.numericInput;
  const typeMatches = !needsTyped || typed === needsTyped;
  const parsedNumber = numericCfg ? Number(numeric) : null;
  const numericValid = !numericCfg || (
    Number.isFinite(parsedNumber)
    && (numericCfg.min == null || parsedNumber >= numericCfg.min)
    && (numericCfg.max == null || parsedNumber <= numericCfg.max)
  );

  const onAccept = () => {
    if (numericCfg) {
      if (!numericValid) return;
      close(parsedNumber);
    } else {
      close(true);
    }
  };

  return (
    <ConfirmContext.Provider value={ctx}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(o) => { if (!o) close(false); }}
      >
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-title">{state?.title}</AlertDialogTitle>
            {state?.description && (
              <AlertDialogDescription data-testid="confirm-description">
                {state.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {needsTyped && (
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                Type <span className="font-mono text-red-600">{needsTyped}</span> to confirm
              </label>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                data-testid="confirm-typed-input"
                className="qb-input h-9 w-full font-mono"
                placeholder={needsTyped}
              />
            </div>
          )}
          {numericCfg && (
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                {numericCfg.label || "Enter a number"}
              </label>
              <input
                type="number"
                value={numeric}
                onChange={(e) => setNumeric(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onAccept(); }}
                autoFocus
                min={numericCfg.min}
                max={numericCfg.max}
                step={numericCfg.step ?? 1}
                data-testid="confirm-numeric-input"
                className="qb-input h-9 w-full font-mono"
              />
              {!numericValid && numeric !== "" && (
                <div className="text-[11px] text-red-600">
                  Please enter a number between {numericCfg.min ?? "—"} and {numericCfg.max ?? "—"}.
                </div>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-cancel"
              onClick={() => close(false)}
            >
              {state?.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-accept"
              onClick={onAccept}
              disabled={!typeMatches || !numericValid}
              className={state?.destructive ? "bg-red-600 hover:bg-red-700" : ""}
            >
              {state?.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx.confirm;
}
